import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../components/Input';
import Modal from '../../../../components/Modal';
import SingleSelect from '../../../../components/SingleSelect';
import SelectAsyncJSX from '../../../../components/SelectAsync';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../components/Notification';
import HttpStatus from '../../../../enums/httpStatus';
import { SMARTSELL_ADMIN_ENVIRONMENT } from '../../../../constants/smartsell';
import { audienceAPI, messageApi, sellersApi } from '../../../../services/requests';
import { Audience } from '../../../../types/apiResponse/audience';
import { Seller } from '../../../../types';
import { SelectOption } from '../../../../utils/getSelectOptions';
import * as s from './styles';

type CreateMessageModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    subject?: string;
    message?: string;
  };
};

const CreateMessageModal = ({ open, onClose, onSuccess, initialData }: CreateMessageModalProps) => {
  const { t, i18n } = useTranslation();

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sendTo, setSendTo] = useState<SelectOption | null>(null);
  const [audienceOption, setAudienceOption] = useState<SelectOption | null>(null);
  const [sellerOptions, setSellerOptions] = useState<SelectOption[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const sendToOptions = useMemo<SelectOption[]>(
    () => [
      {
        label: t('allSellers'),
        value: 'sendToAllSellers',
      },
      {
        label: t('audience'),
        value: 'sendToThisAudience',
      },
      {
        label: t('sellers'),
        value: 'sendToThisSellers',
      },
    ],
    [i18n.language],
  );

  const resetRecipients = () => {
    setAudienceOption(null);
    setSellerOptions(null);
  };

  useEffect(() => {
    if (open) {
      setSubject(initialData?.subject ?? '');
      setContent(initialData?.message ?? '');
      setSendTo(null);
      resetRecipients();
    } else {
      setIsLoading(false);
    }
  }, [open, initialData?.subject, initialData?.message]);

  useEffect(() => {
    if (!sendTo) {
      resetRecipients();
      return;
    }

    if (sendTo.value !== 'sendToThisAudience') {
      setAudienceOption(null);
    }

    if (sendTo.value !== 'sendToThisSellers') {
      setSellerOptions(null);
    }
  }, [sendTo]);

  const handleSubmit = async () => {
    if (!subject.trim()) {
      frontendNotification({ message: t('subjectRequired'), type: 'warning' });
      return;
    }

    if (!content.trim()) {
      frontendNotification({ message: t('messageRequired'), type: 'warning' });
      return;
    }

    if (!sendTo?.value) {
      frontendNotification({ message: t('sendToIsRequired'), type: 'warning' });
      return;
    }

    let recipients: boolean | number | string[] | null = null;

    if (sendTo.value === 'sendToAllSellers') {
      recipients = true;
    }

    if (sendTo.value === 'sendToThisAudience') {
      if (!audienceOption?.value && audienceOption?.value !== 0) {
        frontendNotification({ message: t('audienceIsRequired'), type: 'warning' });
        return;
      }

      recipients = Number(audienceOption.value);
    }

    if (sendTo.value === 'sendToThisSellers') {
      if (!sellerOptions || sellerOptions.length === 0) {
        frontendNotification({ message: t('sellersAreRequired'), type: 'warning' });
        return;
      }

      recipients = sellerOptions.map((option) => option.value as string);
    }

    setIsLoading(true);

    try {
      messageApi.query = '';
      const res = await messageApi.store({
        subject: subject.trim(),
        message: content.trim(),
        registrationOrigin: SMARTSELL_ADMIN_ENVIRONMENT,
        sendTo: sendTo.value,
        recipients,
      });

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      frontendNotification({
        message: t('messageCreatedSuccess'),
        type: 'success',
      });

      onSuccess();
      onClose();
    } catch (error) {
      backendErrorNotification(error as AxiosError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t('newMessage')}
      showModal={open}
      closeModal={onClose}
      cancelButton={onClose}
      actionNameBtn={t('save')}
      action={handleSubmit}
      isLoading={isLoading}
      bodyStyle={{ width: '100%', overflow: 'auto' }}
    >
      <s.Form>
        <s.Row>
          <s.Field>
            <Input
              value={subject}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSubject(event.target.value)
              }
              placeholder={t('subject')}
              label={t('subject') as string}
              disabled={isLoading}
            />
          </s.Field>
        </s.Row>

        <s.Row>
          <s.Field style={{ flex: 1 }}>
            <s.Label htmlFor="message-content">{t('message')}</s.Label>
            <s.Textarea
              id="message-content"
              value={content}
              placeholder={t('message') as string}
              onChange={(event) => setContent(event.target.value)}
              disabled={isLoading}
            />
          </s.Field>
        </s.Row>

        <s.Row>
          <s.Field>
            <SingleSelect
              options={sendToOptions}
              value={sendTo}
              onChange={(option: SelectOption) => setSendTo(option)}
              label={t('sendTo') as string}
              placeholder={t('sendTo') as string}
              isRequired
              disabled={isLoading}
            />
          </s.Field>
        </s.Row>

        {sendTo?.value === 'sendToThisAudience' && (
          <s.Row>
            <s.Field>
              <s.Label htmlFor="audience-select">{t('audience')}</s.Label>
              <SelectAsyncJSX
                id="audience-select"
                value={audienceOption}
                onChange={setAudienceOption}
                request={audienceAPI}
                reqResponseToOption={{
                  mapper: {
                    label: (item: Audience) => `${item.id} - ${item.description}`,
                    value: (item: Audience) => item.id,
                  },
                }}
                disabled={isLoading}
                placeholder={t('audience')}
              />
            </s.Field>
          </s.Row>
        )}

        {sendTo?.value === 'sendToThisSellers' && (
          <s.Row>
            <s.Field>
              <s.Label htmlFor="sellers-select">{t('sellers')}</s.Label>
              <SelectAsyncJSX
                id="sellers-select"
                value={sellerOptions}
                onChange={setSellerOptions}
                request={sellersApi}
                reqResponseToOption={{
                  mapper: {
                    label: (item: Seller) => `${item.CodigoVendedor} - ${item.Nome}`,
                    value: (item: Seller) => item.CodigoVendedor,
                  },
                }}
                disabled={isLoading}
                placeholder={t('selectTheSellers')}
                isMulti
              />
            </s.Field>
          </s.Row>
        )}
      </s.Form>
    </Modal>
  );
};

CreateMessageModal.defaultProps = {
  initialData: undefined,
};

export default CreateMessageModal;
